import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Keyboard } from 'swiper';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Lottie from '../Lottie/Lottie';
import { formatVersionNumber, toVersionNumber } from '../../store/featureUpdate/api';
import { minorUpdates, versionString } from '../../contexts/version';

SwiperCore.use([Keyboard]);

const PLAY_CONFETTI_ON_OPEN = true;

// Crop covers to 16:9 before upload (1920×1080). Image and video share this frame.
export const COVER_ASPECT_RATIO = '16 / 9';

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const WhatsNewDialogContainer = React.forwardRef(function WhatsNewDialogContainer(
  { children, showConfetti, ownerState, style, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      {...props}
      style={{
        alignItems: 'center',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        outline: 0,
        ...style
      }}
    >
      {showConfetti && (
        <Lottie
          aria-hidden
          data-testid="whats-new-confetti"
          options={{
            animationData: '/animations/confetti.json',
            autoplay: true,
            loop: false,
            rendererSettings: { preserveAspectRatio: 'xMidYMid slice' }
          }}
          style={{
            height: '100%',
            inset: 0,
            pointerEvents: 'none',
            position: 'absolute',
            width: '100%'
          }}
        />
      )}
      {children}
    </div>
  );
});

const isInternalPath = path => typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

const isSafeMarkdownLink = href =>
  typeof href === 'string' &&
  (/^(https?:\/\/|mailto:|tel:|#)/i.test(href) || (href.startsWith('/') && !href.startsWith('//')));

const isExternalLink = href => /^https?:\/\//i.test(href);

const visuallyHidden = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
};

const SafeMarkdownLink = ({ href, children, ...props }) => {
  if (!isSafeMarkdownLink(href)) return <span>{children}</span>;

  return (
    <a
      {...props}
      href={href}
      target={isExternalLink(href) ? '_blank' : undefined}
      rel={isExternalLink(href) ? 'noopener noreferrer' : undefined}
    >
      {children}
    </a>
  );
};

const DESCRIPTION_SCROLL_FADE_PX = 10;
const minorUpdateBodySx = { color: 'text.secondary', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-line' };
const LATEST_RELEASE_COUNT = 3;
const NAV_HIT = 16;
const NAV_GAP = 7;
const NAV_PILL = 22;
const NAV_DOT = 8;

const navPillX = index => index * (NAV_HIT + NAV_GAP) + (NAV_HIT - NAV_PILL) / 2;

const navPillTransform = x => `translate3d(${x}px, -50%, 0)`;

const versionReleases = update => {
  const releases = Array.isArray(update?.releases) && update.releases.length ? update.releases : update ? [update] : [];
  return releases.slice(0, LATEST_RELEASE_COUNT);
};

const WhatsNewDialog = ({
  open,
  onClose,
  onDone,
  onFeatureClick,
  onFeatureViewed,
  intl,
  status,
  update,
  resume,
  isClient
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [descriptionScrolled, setDescriptionScrolled] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionMenuAnchor, setVersionMenuAnchor] = useState(null);
  const [showMinorUpdates, setShowMinorUpdates] = useState(false);
  const swiperRef = useRef(null);
  const videoRefs = useRef([]);
  const descriptionRefs = useRef([]);
  const pillRef = useRef(null);
  const pillFromRef = useRef(0);
  const pillSnapRef = useRef(true);
  const locale = ['zh', 'zh-Hant-HK'].includes(intl.locale) ? intl.locale : 'en';
  const updates = minorUpdates.filter(
    entry =>
      entry.content[isClient ? 'client' : 'adviser']?.[locale]?.trim() &&
      toVersionNumber(entry.version) <= toVersionNumber(versionString)
  );
  const releases = versionReleases(update);
  const activeRelease = releases.find(release => release.version === selectedVersion) || releases[0] || null;
  const features = activeRelease?.features || [];
  const activeVersion = activeRelease?.version;
  const activeFeatureId = features[activeSlide]?.id;

  const pauseVideos = () => {
    videoRefs.current.forEach(video => {
      if (video && typeof video.pause === 'function') video.pause();
    });
  };

  useEffect(() => {
    if (!open) {
      pauseVideos();
      swiperRef.current = null;
      return;
    }

    const available = versionReleases(update);
    const match = resume && available.find(release => release.version === resume.version);
    const version = match ? match.version : update?.version ?? null;
    const count = (match || available[0])?.features?.length || 0;
    const slide = match && count ? Math.min(Math.max(resume.slide || 0, 0), count - 1) : 0;
    setSelectedVersion(version);
    setVersionMenuAnchor(null);
    setActiveSlide(slide);
    setShowMinorUpdates(false);
  }, [open, update?.version]);

  useEffect(() => {
    if (!open || !swiperRef.current) return;
    if (swiperRef.current.activeIndex !== activeSlide) swiperRef.current.slideTo(activeSlide, 0);
  }, [open, activeSlide, activeVersion]);

  useEffect(() => {
    if (!open || status !== 'ready' || activeFeatureId == null) return;
    if (onFeatureViewed) onFeatureViewed(activeFeatureId);
  }, [open, status, activeFeatureId, onFeatureViewed]);

  useLayoutEffect(() => {
    descriptionRefs.current.forEach(el => {
      if (el) el.scrollTop = 0;
    });
    setDescriptionScrolled(false);
  }, [activeSlide]);

  useLayoutEffect(() => {
    pillSnapRef.current = true;
  }, [open, activeVersion]);

  useLayoutEffect(() => {
    const el = pillRef.current;
    if (!el) return;

    const to = navPillX(activeSlide);
    const from = pillFromRef.current;
    const snap = pillSnapRef.current || from === to || prefersReducedMotion() || typeof el.animate !== 'function';
    pillSnapRef.current = false;
    pillFromRef.current = to;

    if (snap) {
      if (el.getAnimations) el.getAnimations().forEach(animation => animation.cancel());
      el.style.transform = navPillTransform(to);
      return;
    }

    const live = window.getComputedStyle(el).transform;
    if (el.getAnimations) el.getAnimations().forEach(animation => animation.cancel());
    if (live && live !== 'none') el.style.transform = live;

    const stretch = Math.min(1.65, 1 + Math.abs(to - from) / 56);
    const animation = el.animate(
      [
        { transform: el.style.transform },
        { transform: `${navPillTransform((from + to) / 2)} scaleX(${stretch})`, offset: 0.42 },
        { transform: navPillTransform(to) }
      ],
      { duration: 320, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
    );
    if (animation.finished)
      animation.finished
        .then(() => {
          el.style.transform = navPillTransform(to);
        })
        .catch(() => {});
  }, [activeSlide, activeVersion, open]);

  useEffect(() => {
    if (!open) return;

    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      video.muted = true;
      if (index !== activeSlide) {
        if (typeof video.pause === 'function') video.pause();
        return;
      }
      const play = video.play?.();
      if (play && typeof play.catch === 'function') play.catch(() => {});
    });
  }, [open, activeSlide, activeVersion]);

  const close = () => {
    pauseVideos();
    onClose();
  };

  const finish = () => {
    pauseVideos();
    (onDone || onClose)();
  };

  const handleFeatureClick = path => {
    if (isInternalPath(path)) {
      pauseVideos();
      onFeatureClick(path);
    }
  };

  const formatMessage = id => intl.formatMessage({ id });
  const activeFeature = features[activeSlide];
  const isLastSlide = features.length > 0 && activeSlide >= features.length - 1;
  const goNext = () => swiperRef.current?.slideTo(Math.min(activeSlide + 1, features.length - 1));
  const showConfetti = PLAY_CONFETTI_ON_OPEN && open && status === 'ready' && !prefersReducedMotion();

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth={false}
      aria-labelledby="whats-new-title"
      slots={{ container: WhatsNewDialogContainer }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)'
          }
        },
        container: { showConfetti }
      }}
      sx={theme => ({
        '& .MuiDialog-container': {
          alignItems: 'center',
          padding: { xs: 1, sm: 3 },
          position: 'relative'
        },
        '& .MuiPaper-root': {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
          borderRadius: { xs: '20px', sm: '30px' },
          boxShadow: 'none',
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: 'min(780px, calc(100dvh - 48px))' },
          overflow: 'hidden',
          width: { xs: 'calc(100vw - 16px)', sm: 'min(760px, calc(100vw - 48px))' },
          zIndex: 1
        }
      })}
    >
      <DialogTitle id="whats-new-title" sx={visuallyHidden}>
        {formatMessage('whats-new-title')}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, p: 0, position: 'relative' }}>
        <Button
          id="whats-new-version-button"
          aria-haspopup="menu"
          aria-expanded={versionMenuAnchor ? 'true' : undefined}
          aria-controls={versionMenuAnchor ? 'whats-new-version-menu' : undefined}
          onClick={event => setVersionMenuAnchor(event.currentTarget)}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: '18px !important' }} />}
          sx={theme => ({
            position: 'absolute',
            top: { xs: 12, sm: 16 },
            left: { xs: 12, sm: 16 },
            zIndex: 2,
            height: 26,
            minWidth: 0,
            px: 1.25,
            py: 0,
            backdropFilter: 'blur(8px)',
            borderRadius: '16px',
            backgroundColor: showMinorUpdates ? '#fff' : alpha('#fff', 0.6),
            color: theme.palette.secondary.dark,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.28)}`,
            boxShadow: `0 6px 16px ${alpha(theme.palette.secondary.main, 0.16)}`,
            fontSize: '12px',
            fontWeight: 800,
            letterSpacing: '0.02em',
            lineHeight: 1,
            textTransform: 'none',
            '&:hover': { backgroundColor: alpha('#fff', 0.9) },
            '& .MuiButton-endIcon': { ml: 0.25, mr: -0.25 }
          })}
        >
          {showMinorUpdates
            ? formatMessage('whats-new-minor-updates')
            : activeVersion
            ? formatVersionNumber(activeVersion)
            : formatMessage('whats-new-title')}
        </Button>
        <Menu
          id="whats-new-version-menu"
          anchorEl={versionMenuAnchor}
          open={Boolean(versionMenuAnchor)}
          onClose={() => setVersionMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            list: { 'aria-labelledby': 'whats-new-version-button' },
            paper: { sx: { borderRadius: '12px', minWidth: 140, mt: 0.75 } }
          }}
          sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
        >
          {releases.map(release => (
            <MenuItem
              key={release.version}
              selected={!showMinorUpdates && release.version === activeVersion}
              onClick={() => {
                const sameRelease = release.version === activeVersion;
                if (!sameRelease) {
                  setSelectedVersion(release.version);
                  setActiveSlide(0);
                }
                setShowMinorUpdates(false);
                setVersionMenuAnchor(null);
              }}
              sx={{ fontSize: '14px', fontWeight: 700 }}
            >
              {formatVersionNumber(release.version)}
            </MenuItem>
          ))}
          <MenuItem
            selected={showMinorUpdates}
            onClick={() => {
              setVersionMenuAnchor(null);
              setShowMinorUpdates(true);
            }}
            sx={{ fontSize: '14px', fontWeight: 700 }}
          >
            {formatMessage('whats-new-minor-updates')}
          </MenuItem>
        </Menu>
        {showMinorUpdates && (
          <Box
            aria-labelledby="whats-new-version-button"
            data-testid="whats-new-minor-updates"
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 300,
              pb: updates.length ? { xs: 2.5, sm: 4 } : 4,
              pt: updates.length ? { xs: '52px', sm: '60px' } : 4,
              px: { xs: 2.5, sm: 4 }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {!updates.length && (
                <Typography
                  sx={{
                    ...minorUpdateBodySx,
                    alignItems: 'center',
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    textAlign: 'center'
                  }}
                >
                  {formatMessage('whats-new-no-minor-updates')}
                </Typography>
              )}
              {updates.map(({ version, content }) => (
                <Box key={version} sx={{ borderBottom: theme => `1px solid ${theme.palette.divider}`, py: 1.5 }}>
                  <Typography sx={{ color: 'text.primary', fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                    {version}
                  </Typography>
                  <Typography sx={minorUpdateBodySx}>{content[isClient ? 'client' : 'adviser'][locale]}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {!showMinorUpdates && status === 'loading' && (
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 300, p: 4 }}>
            <Typography role="status" sx={{ color: 'text.primary', fontWeight: 600, textAlign: 'center' }}>
              {formatMessage('whats-new-loading')}
            </Typography>
          </Box>
        )}
        {!showMinorUpdates && status === 'empty' && (
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 300, p: 4 }}>
            <Typography role="status" sx={{ color: 'text.primary', fontWeight: 600, textAlign: 'center' }}>
              {formatMessage('whats-new-empty')}
            </Typography>
          </Box>
        )}
        {!showMinorUpdates && status === 'error' && (
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: 300, p: 4 }}>
            <Typography role="alert" sx={{ color: 'error.main', fontWeight: 600, textAlign: 'center' }}>
              {formatMessage('whats-new-error')}
            </Typography>
          </Box>
        )}
        {!showMinorUpdates && status === 'ready' && update && features.length > 0 && (
          <Stack sx={{ minHeight: 0, position: 'relative' }}>
            <Box
              sx={{
                borderBottomLeftRadius: { xs: '20px', sm: '30px' },
                borderBottomRightRadius: { xs: '20px', sm: '30px' },
                overflow: 'hidden',
                position: 'relative',
                '&::after': {
                  background: 'linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.28))',
                  bottom: 0,
                  content: '""',
                  height: 100,
                  left: 0,
                  pointerEvents: 'none',
                  position: 'absolute',
                  right: 0,
                  zIndex: 10
                }
              }}
            >
              <Swiper
                key={activeVersion}
                slidesPerView={1}
                loop={false}
                autoplay={false}
                allowTouchMove={features.length > 1}
                keyboard={{ enabled: true }}
                resizeObserver
                onSwiper={swiper => {
                  swiperRef.current = swiper;
                }}
                onSlideChange={swiper => {
                  setActiveSlide(swiper.activeIndex);
                }}
                style={{ width: '100%' }}
              >
                {features.map((feature, index) => (
                  <SwiperSlide key={feature.id}>
                    <Box
                      aria-label={intl.formatMessage(
                        { id: 'whats-new-slide-label' },
                        { current: index + 1, total: features.length }
                      )}
                      data-cover-ratio={COVER_ASPECT_RATIO}
                      sx={theme => ({
                        aspectRatio: COVER_ASPECT_RATIO,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.35)} 0%, ${alpha(
                          theme.palette.secondary.main,
                          0.22
                        )} 100%)`,
                        overflow: 'hidden',
                        position: 'relative',
                        userSelect: 'none',
                        WebkitUserSelect: 'none',
                        width: '100%'
                      })}
                    >
                      {feature.media.kind === 'video' ? (
                        <video
                          ref={video => {
                            videoRefs.current[index] = video;
                          }}
                          src={feature.media.url}
                          aria-label={feature.media.alt || formatMessage('whats-new-cover-alt')}
                          draggable={false}
                          autoPlay={activeSlide === index}
                          loop
                          defaultMuted
                          playsInline
                          preload="metadata"
                          style={{ height: '100%', objectFit: 'cover', width: '100%' }}
                        />
                      ) : (
                        <Box
                          component="img"
                          src={feature.media.url}
                          alt={feature.media.alt || formatMessage('whats-new-cover-alt')}
                          loading="lazy"
                          draggable={false}
                          sx={{ height: '100%', objectFit: 'cover', width: '100%' }}
                        />
                      )}
                    </Box>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
            <Box sx={{ overflow: 'hidden', position: 'relative' }}>
              {features.map((feature, index) => {
                const active = activeSlide === index;

                return (
                  <Box
                    key={feature.id}
                    aria-hidden={!active}
                    sx={{
                      left: 0,
                      opacity: active ? 1 : 0,
                      pointerEvents: active ? 'auto' : 'none',
                      position: active ? 'relative' : 'absolute',
                      right: 0,
                      top: 0,
                      transition: active
                        ? 'opacity 240ms ease, visibility 0s linear'
                        : 'opacity 240ms ease, visibility 0s linear 240ms',
                      visibility: active ? 'visible' : 'hidden',
                      width: '100%',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        data-testid="whats-new-description"
                        ref={el => {
                          descriptionRefs.current[index] = el;
                        }}
                        onScroll={event => {
                          if (!active) return;
                          setDescriptionScrolled(event.currentTarget.scrollTop > DESCRIPTION_SCROLL_FADE_PX);
                        }}
                        sx={{
                          height: 240,
                          overflowY: 'auto',
                          pb: { xs: 2.5, sm: 3.5 },
                          pt: { xs: 2.5, sm: 4 },
                          px: { xs: 2.5, sm: 4 }
                        }}
                      >
                        <Typography
                          variant="h6"
                          component="h2"
                          sx={{
                            color: 'text.primary',
                            fontSize: { xs: '1.55rem', sm: '2.15rem' },
                            fontWeight: 800,
                            letterSpacing: '-0.035em',
                            lineHeight: 1.1,
                            mb: 0,
                            pb: '12px'
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Box
                          sx={theme => ({
                            color: theme.palette.text.secondary,
                            fontSize: '16px',
                            lineHeight: 1.75,
                            pb: 5,
                            '& p': { m: 0 },
                            '& p + p': { mt: 1.25 },
                            '& ul, & ol': { listStylePosition: 'outside', mb: 0, mt: 1, pl: 2.5 },
                            '& ul, & ul li': { listStyleType: 'disc' },
                            '& ol, & ol li': { listStyleType: 'decimal' },
                            '& li': { mb: 0.5, p: 0 },
                            '& strong': { color: theme.palette.text.primary, fontWeight: 800 },
                            '& em': { fontStyle: 'italic' },
                            '& h1, & h2, & h3': {
                              color: theme.palette.text.primary,
                              fontWeight: 800,
                              letterSpacing: '-0.02em',
                              lineHeight: 1.2,
                              mb: 1,
                              mt: 2
                            },
                            '& h1': { fontSize: '1.55em' },
                            '& h2': { fontSize: '1.35em' },
                            '& h3': { fontSize: '1.15em' },
                            '& blockquote': {
                              backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                              borderLeft: `2px solid ${theme.palette.secondary.main}`,
                              color: theme.palette.text.secondary,
                              m: 0,
                              my: 1.5,
                              px: 1.5,
                              py: 1
                            },
                            '& code': {
                              backgroundColor: theme.palette.action.hover,
                              borderRadius: 1,
                              color: theme.palette.text.primary,
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              fontSize: '0.9em',
                              px: 0.6,
                              py: 0.2
                            },
                            '& pre': {
                              backgroundColor: theme.palette.grey[900],
                              borderRadius: 2,
                              color: theme.palette.common.white,
                              overflowX: 'auto',
                              p: 1.5,
                              '& code': {
                                backgroundColor: 'transparent',
                                color: 'inherit',
                                p: 0
                              }
                            },
                            '& table': { borderCollapse: 'collapse', my: 1.5, width: '100%' },
                            '& th, & td': { border: `1px solid ${theme.palette.divider}`, p: 1, textAlign: 'left' },
                            '& a': { color: theme.palette.secondary.main, fontWeight: 700, textUnderlineOffset: '3px' }
                          })}
                        >
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            skipHtml
                            disallowedElements={['img']}
                            components={{
                              a: SafeMarkdownLink,
                              img: () => null
                            }}
                          >
                            {feature.description}
                          </ReactMarkdown>
                        </Box>
                      </Box>
                      {active && descriptionScrolled && (
                        <Box
                          aria-hidden
                          data-testid="whats-new-description-fade"
                          sx={theme => ({
                            background: `linear-gradient(to bottom, ${theme.palette.background.paper}, transparent)`,
                            height: 48,
                            left: 0,
                            pointerEvents: 'none',
                            position: 'absolute',
                            right: 0,
                            top: 0
                          })}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Stack>
        )}
      </DialogContent>
      {showMinorUpdates && (
        <DialogActions
          sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, justifyContent: 'flex-start', p: 2.5 }}
        >
          <Button
            onClick={() => setShowMinorUpdates(false)}
            sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'none' }}
          >
            {formatMessage('whats-new-back')}
          </Button>
        </DialogActions>
      )}
      {!showMinorUpdates && status === 'ready' && update && features.length > 0 && (
        <DialogActions
          sx={theme => ({
            alignItems: { xs: 'stretch', sm: 'center' },
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 1.5 },
            justifyContent: 'space-between',
            p: { xs: 2.5, sm: 3.25 },
            pt: { xs: 2, sm: 2.5 }
          })}
        >
          <Stack direction="row" aria-label={formatMessage('whats-new-navigation')} sx={{ alignItems: 'center' }}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: `${NAV_GAP}px`, position: 'relative' }}>
              <Box
                ref={pillRef}
                aria-hidden
                data-testid="whats-new-nav-pill"
                sx={theme => ({
                  backgroundColor: theme.palette.secondary.main,
                  backgroundImage: `linear-gradient(90deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
                  borderRadius: 999,
                  boxShadow: `0 2px 6px ${alpha(theme.palette.secondary.main, 0.32)}`,
                  height: NAV_DOT,
                  left: 0,
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: '50%',
                  transform: navPillTransform(navPillX(activeSlide)),
                  width: NAV_PILL,
                  zIndex: 2
                })}
              />
              {features.map((feature, index) => (
                <Button
                  key={feature.id}
                  disableRipple
                  aria-label={`${formatMessage('whats-new-go-to-slide')} ${index + 1}`}
                  aria-current={activeSlide === index ? 'true' : undefined}
                  onClick={() => swiperRef.current?.slideTo(index)}
                  sx={theme => ({
                    '&:focus-visible': {
                      outline: `3px solid ${alpha(theme.palette.secondary.main, 0.35)}`,
                      outlineOffset: 2
                    },
                    '&:hover': { backgroundColor: 'transparent' },
                    '&:hover > span': { backgroundColor: alpha(theme.palette.secondary.main, 0.45) },
                    backgroundColor: 'transparent',
                    borderRadius: 999,
                    flexShrink: 0,
                    height: NAV_HIT,
                    minWidth: NAV_HIT,
                    p: 0,
                    width: NAV_HIT
                  })}
                >
                  <Box
                    component="span"
                    sx={theme => ({
                      backgroundColor: alpha(theme.palette.secondary.main, 0.22),
                      borderRadius: 999,
                      height: NAV_DOT,
                      transition: 'background-color 140ms ease',
                      width: NAV_DOT,
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
                    })}
                  />
                </Button>
              ))}
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: '14px', fontWeight: 500, ml: '16px' }}>
              {activeSlide + 1} / {features.length}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
            {activeFeature && isInternalPath(activeFeature.path) && (
              <Button
                aria-label={formatMessage('whats-new-learn-more')}
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => handleFeatureClick(activeFeature.path)}
                variant="outlined"
                color="secondary"
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  minWidth: 132,
                  px: 2.25,
                  py: 1,
                  textTransform: 'none'
                }}
              >
                {formatMessage('whats-new-learn-more')}
              </Button>
            )}
            {isLastSlide ? (
              <Button
                onClick={finish}
                variant="contained"
                color="secondary"
                sx={theme => ({
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                    boxShadow: `0 8px 18px ${alpha(theme.palette.secondary.main, 0.28)}`
                  },
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 999,
                  boxShadow: `0 5px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
                  color: theme.palette.secondary.contrastText,
                  fontWeight: 700,
                  minWidth: 92,
                  px: 2.25,
                  py: 1,
                  textTransform: 'none'
                })}
              >
                {formatMessage('whats-new-done')}
              </Button>
            ) : (
              <Button
                onClick={goNext}
                variant="contained"
                color="secondary"
                sx={theme => ({
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                    boxShadow: `0 8px 18px ${alpha(theme.palette.secondary.main, 0.28)}`
                  },
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 999,
                  boxShadow: `0 5px 12px ${alpha(theme.palette.secondary.main, 0.2)}`,
                  color: theme.palette.secondary.contrastText,
                  fontWeight: 700,
                  minWidth: 92,
                  px: 2.25,
                  py: 1,
                  textTransform: 'none'
                })}
              >
                {formatMessage('whats-new-next')}
              </Button>
            )}
          </Stack>
        </DialogActions>
      )}
      {!showMinorUpdates && status !== 'ready' && (
        <DialogActions
          sx={{ borderTop: theme => `1px solid ${theme.palette.divider}`, justifyContent: 'flex-end', p: 2.5 }}
        >
          <Button
            onClick={close}
            sx={{ borderRadius: 999, color: 'text.primary', fontWeight: 700, textTransform: 'none' }}
          >
            {formatMessage('Close')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export { isInternalPath };
export default WhatsNewDialog;
